import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  Autocomplete,
  FormHelperText,
  IconButton,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import MasterClassValidationSchema from "../Validations/MasterClassValidation";
import { toast } from "react-toastify";
import {
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
  BUTTON_STYLES,
  FORM_STYLES,
} from "../../styles/componentStyles";
import api from "../../axiosInstance";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

// Default values
const DEFAULT_MESSAGE_FOR_PURCHASED = `You already have full access to this subject.

Review your notes, PYQs, and LMRs before class
to strengthen your understanding.`;

const DEFAULT_MESSAGE_FOR_NOT_PURCHASED = `Purchase the full subject to access study notes,

solved PYQs, and LMRs designed to help you grasp
every concept taught in class.`;

const DEFAULT_TIPS = ["Tip : Revising before class helps you follow concepts easily."];

const DEFAULT_BUTTON_TEXT = "Join Upcoming Live Class";

function MasterClassModal({ open, onClose, masterClassEdit, onSuccess }) {
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [regulationYears, setRegulationYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [books, setBooks] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);

  // Form validation using useFormik hook
  const isEditMode = masterClassEdit && masterClassEdit.id;
  const formik = useFormik({
    initialValues: {
      title: masterClassEdit?.title || "",
      facultyName: masterClassEdit?.facultyName || "",
      facultyImage: masterClassEdit?.facultyImage || "",
      subject: masterClassEdit?.subject || "",
      experienceYears: masterClassEdit?.experienceYears || "",
      scheduledDate: masterClassEdit?.scheduledDate
        ? masterClassEdit.scheduledDate.split("T")[0]
        : "",
      scheduledTime: masterClassEdit?.scheduledTime
        ? (typeof masterClassEdit.scheduledTime === 'string' 
            ? masterClassEdit.scheduledTime.slice(0, 5) 
            : new Date(masterClassEdit.scheduledTime).toTimeString().slice(0, 5))
        : "",
      messageForPurchased: isEditMode
        ? masterClassEdit?.messageForPurchased || ""
        : DEFAULT_MESSAGE_FOR_PURCHASED,
      messageForNotPurchased: isEditMode
        ? masterClassEdit?.messageForNotPurchased || ""
        : DEFAULT_MESSAGE_FOR_NOT_PURCHASED,
      tips: isEditMode
        ? masterClassEdit?.tips || []
        : DEFAULT_TIPS,
      imageUrl: masterClassEdit?.imageUrl || "",
      buttonText: isEditMode
        ? masterClassEdit?.buttonText || ""
        : DEFAULT_BUTTON_TEXT,
      bookId: masterClassEdit?.bookId || null,
      universityId: masterClassEdit?.universityId || null,
      courseId: masterClassEdit?.courseId || null,
      semesterId: masterClassEdit?.semesterId || null,
      regulationYearId: masterClassEdit?.regulationYearId || null,
      active: masterClassEdit?.active ?? true,
    },

    validationSchema: MasterClassValidationSchema,
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      try {
        // Transform form data to match API request body
        // scheduledTime should be in HH:mm:ss format (LocalTime in Java)
        const timeString = values.scheduledTime || "";
        const scheduledTimeFormatted = timeString.length === 5 
          ? `${timeString}:00` // Add seconds if only HH:mm format
          : timeString;

        // Ensure tips is a clean array of non-empty strings for JSONB
        // The backend should convert this array to JSONB automatically
        const tipsArray = values.tips && Array.isArray(values.tips)
          ? values.tips.filter(tip => tip && tip.trim().length > 0)
          : [];

        const payload = {
          title: values.title,
          facultyName: values.facultyName,
          facultyImage: values.facultyImage || null,
          subject: values.subject || null,
          experienceYears: values.experienceYears
            ? parseInt(values.experienceYears)
            : null,
          scheduledDate: values.scheduledDate,
          scheduledTime: scheduledTimeFormatted,
          messageForPurchased: values.messageForPurchased || null,
          messageForNotPurchased: values.messageForNotPurchased || null,
          tips: tipsArray, // JavaScript array - axios will serialize to JSON array
          imageUrl: values.imageUrl || null,
          buttonText: values.buttonText || null,
          bookId: values.bookId || null,
          universityId: values.universityId || null,
          courseId: values.courseId || null,
          semesterId: values.semesterId || null,
          regulationYearId: values.regulationYearId || null,
          active: values.active,
        };

        // Debug: Log payload to verify tips is an array
        console.log("Master Class Payload:", {
          ...payload,
          tips: payload.tips,
          tipsType: typeof payload.tips,
          tipsIsArray: Array.isArray(payload.tips)
        });

        let response;
        // If it's Edit data it will call Update api
        if (masterClassEdit?.id) {
          response = await api.put(
            `/api/v1/master-classes/${masterClassEdit.id}`,
            payload
          );
        } else {
          // Else it's Add data it will call Add api
          response = await api.post("/api/v1/master-classes", payload);
        }

        // Show success toast
        toast.success(
          `Master Class ${
            masterClassEdit?.id ? "updated" : "added"
          } successfully!`,
          {
            position: "top-right",
            autoClose: 2000,
          }
        );

        if (onSuccess) {
          onSuccess(response.data.data || response.data);
        }
        resetForm();
        onClose();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "Failed to submit. Please try again.",
          {
            autoClose: 3000,
          }
        );
        console.error("API Error:", error);
      }
    },
  });

  // Fetch all universities
  useEffect(() => {
    api
      .get("/getAllUniversities")
      .then((res) => setUniversities(res.data.data || []))
      .catch((err) => console.error("Error fetching universities:", err));
  }, []);

  // Fetch all courses
  useEffect(() => {
    api
      .get("/getCourses", { params: { active: true } })
      .then((res) => setCourses(res.data.data || []))
      .catch((err) => console.error("Error fetching courses:", err));
  }, []);

  // Fetch all semesters
  useEffect(() => {
    api
      .get("/getSemesters")
      .then((res) => setSemesters(res.data.data || []))
      .catch((err) => console.error("Error fetching semesters:", err));
  }, []);

  // Fetch all books
  useEffect(() => {
    api
      .get("/api/v1/books")
      .then((res) => setBooks(res.data.data || []))
      .catch((err) => console.error("Error fetching books:", err));
  }, []);

  // Filter courses based on selected university
  useEffect(() => {
    if (formik.values.universityId) {
      const filtered = courses.filter(
        (course) => course.universityId === parseInt(formik.values.universityId)
      );
      setFilteredCourses(filtered);
      // Reset courseId if current selection is not in filtered list
      if (
        formik.values.courseId &&
        !filtered.find((c) => c.id === formik.values.courseId)
      ) {
        formik.setFieldValue("courseId", null);
        formik.setFieldValue("regulationYearId", null);
      }
    } else {
      setFilteredCourses([]);
      formik.setFieldValue("courseId", null);
      formik.setFieldValue("regulationYearId", null);
    }
  }, [formik.values.universityId, courses]);

  // Fetch regulation years when course and university are selected
  useEffect(() => {
    if (formik.values.courseId && formik.values.universityId) {
      api
        .get(
          `/api/regulation-years/course/${formik.values.courseId}/university/${formik.values.universityId}`
        )
        .then((res) => {
          setRegulationYears(res.data.data || []);
          // Reset regulationYearId if current selection is not in the list
          if (
            formik.values.regulationYearId &&
            !res.data.data.find((r) => r.id === formik.values.regulationYearId)
          ) {
            formik.setFieldValue("regulationYearId", null);
            formik.setFieldValue("bookId", null);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch regulation years:", err);
          setRegulationYears([]);
        });
    } else {
      setRegulationYears([]);
      formik.setFieldValue("regulationYearId", null);
      formik.setFieldValue("bookId", null);
    }
  }, [formik.values.courseId, formik.values.universityId]);

  // Filter books based on regulation (if API supports, otherwise show all)
  useEffect(() => {
    if (formik.values.regulationYearId) {
      // For now, show all books. If API supports filtering by regulation, use:
      // api.get(`/api/v1/books?regulationYearId=${formik.values.regulationYearId}`)
      setFilteredBooks(books);
    } else {
      setFilteredBooks([]);
      formik.setFieldValue("bookId", null);
    }
  }, [formik.values.regulationYearId, books]);

  // Auto-populate title and subject when book is selected (only for new entries)
  useEffect(() => {
    if (formik.values.bookId && !masterClassEdit?.id) {
      const selectedBook = books.find((b) => b.id === formik.values.bookId);
      if (selectedBook) {
        const bookTitle = selectedBook.title || "";
        // Auto-populate title: "Book Name Live Class"
        if (!formik.values.title || formik.values.title === "") {
          formik.setFieldValue("title", `${bookTitle} Live Class`);
        }
        // Auto-populate subject: book name
        if (!formik.values.subject || formik.values.subject === "") {
          formik.setFieldValue("subject", bookTitle);
        }
      }
    }
  }, [formik.values.bookId, books, masterClassEdit]);

  // Handle tips array
  const handleAddTip = () => {
    const newTips = [...(formik.values.tips || []), ""];
    formik.setFieldValue("tips", newTips);
  };

  const handleRemoveTip = (index) => {
    const newTips = formik.values.tips.filter((_, i) => i !== index);
    formik.setFieldValue("tips", newTips);
  };

  const handleTipChange = (index, value) => {
    const newTips = [...formik.values.tips];
    newTips[index] = value;
    formik.setFieldValue("tips", newTips);
  };

  return (
    <>
      <Modal open={open} onClose={onClose} disableScrollLock>
        <Box
          sx={{ ...MODAL_STYLES.large, width: { xs: 340, md: 800 } }}
          component="form"
          onSubmit={formik.handleSubmit}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            {masterClassEdit && Object.keys(masterClassEdit).length > 0
              ? "Update Master Class"
              : "Add Master Class"}
          </Typography>

          <Box sx={FORM_STYLES.container}>
            {/* Cascading Dropdowns - First Section */}
            <Typography variant="h6" sx={{ mt: 0, mb: 1, fontWeight: 600 }}>
              Course & Book Selection
            </Typography>

            <FormControl
              {...TEXT_FIELD_STYLES.standard}
              error={
                formik.touched.universityId &&
                Boolean(formik.errors.universityId)
              }
            >
              <InputLabel>University</InputLabel>
              <Select
                label="University"
                name="universityId"
                value={formik.values.universityId || ""}
                onChange={(e) => {
                  formik.setFieldValue("universityId", e.target.value || null);
                  formik.setFieldValue("courseId", null);
                  formik.setFieldValue("regulationYearId", null);
                  formik.setFieldValue("bookId", null);
                }}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {universities.map((university) => (
                  <MenuItem key={university.id} value={university.id}>
                    {university.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.universityId && formik.errors.universityId && (
                <FormHelperText>{formik.errors.universityId}</FormHelperText>
              )}
            </FormControl>

            <FormControl
              {...TEXT_FIELD_STYLES.standard}
              error={
                formik.touched.courseId && Boolean(formik.errors.courseId)
              }
              disabled={!formik.values.universityId}
            >
              <InputLabel>Course</InputLabel>
              <Select
                label="Course"
                name="courseId"
                value={formik.values.courseId || ""}
                onChange={(e) => {
                  formik.setFieldValue("courseId", e.target.value || null);
                  formik.setFieldValue("regulationYearId", null);
                  formik.setFieldValue("bookId", null);
                }}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {filteredCourses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.courseName}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.courseId && formik.errors.courseId && (
                <FormHelperText>{formik.errors.courseId}</FormHelperText>
              )}
            </FormControl>

            <FormControl
              {...TEXT_FIELD_STYLES.standard}
              error={
                formik.touched.regulationYearId &&
                Boolean(formik.errors.regulationYearId)
              }
              disabled={!formik.values.courseId || !formik.values.universityId}
            >
              <InputLabel>Regulation Year</InputLabel>
              <Select
                label="Regulation Year"
                name="regulationYearId"
                value={formik.values.regulationYearId || ""}
                onChange={(e) => {
                  formik.setFieldValue(
                    "regulationYearId",
                    e.target.value || null
                  );
                  formik.setFieldValue("bookId", null);
                }}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {regulationYears.map((regulation) => (
                  <MenuItem key={regulation.id} value={regulation.id}>
                    {regulation.regulationName}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.regulationYearId &&
                formik.errors.regulationYearId && (
                  <FormHelperText>
                    {formik.errors.regulationYearId}
                  </FormHelperText>
                )}
            </FormControl>

            <Box sx={FORM_STYLES.row}>
              <FormControl
                {...TEXT_FIELD_STYLES.standard}
                error={
                  formik.touched.semesterId && Boolean(formik.errors.semesterId)
                }
                sx={FORM_STYLES.field.sx}
              >
                <InputLabel>Semester</InputLabel>
                <Select
                  label="Semester"
                  name="semesterId"
                  value={formik.values.semesterId || ""}
                  onChange={(e) => {
                    formik.setFieldValue("semesterId", e.target.value || null);
                  }}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {semesters.map((semester) => (
                    <MenuItem key={semester.id} value={semester.id}>
                      {semester.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.semesterId && formik.errors.semesterId && (
                  <FormHelperText>{formik.errors.semesterId}</FormHelperText>
                )}
              </FormControl>

              <FormControl
                {...TEXT_FIELD_STYLES.standard}
                error={formik.touched.bookId && Boolean(formik.errors.bookId)}
                disabled={!formik.values.regulationYearId}
                sx={FORM_STYLES.field.sx}
              >
                <Autocomplete
                  options={filteredBooks}
                  getOptionLabel={(option) =>
                    `${option.id} - ${option.title || "Untitled"}`
                  }
                  value={
                    filteredBooks.find(
                      (b) => b.id === formik.values.bookId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("bookId", newValue ? newValue.id : null);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!formik.values.regulationYearId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Book"
                      variant="outlined"
                      error={
                        formik.touched.bookId && Boolean(formik.errors.bookId)
                      }
                      helperText={
                        formik.touched.bookId && formik.errors.bookId
                      }
                    />
                  )}
                />
              </FormControl>
            </Box>

            {/* Basic Information */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Basic Information
            </Typography>

            <TextField
              label="Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              {...TEXT_FIELD_STYLES.standard}
              required
            />

            <Box sx={FORM_STYLES.row}>
              <TextField
                label="Faculty Name"
                name="facultyName"
                value={formik.values.facultyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.facultyName &&
                  Boolean(formik.errors.facultyName)
                }
                helperText={
                  formik.touched.facultyName && formik.errors.facultyName
                }
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
                required
              />
              <TextField
                label="Subject"
                name="subject"
                value={formik.values.subject}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.subject && Boolean(formik.errors.subject)
                }
                helperText={formik.touched.subject && formik.errors.subject}
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
              />
            </Box>

            <Box sx={FORM_STYLES.row}>
              <TextField
                label="Experience Years"
                name="experienceYears"
                type="number"
                value={formik.values.experienceYears}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.experienceYears &&
                  Boolean(formik.errors.experienceYears)
                }
                helperText={
                  formik.touched.experienceYears && formik.errors.experienceYears
                }
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Button Text"
                name="buttonText"
                value={formik.values.buttonText}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.buttonText && Boolean(formik.errors.buttonText)
                }
                helperText={
                  formik.touched.buttonText && formik.errors.buttonText
                }
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
              />
            </Box>

            {/* Schedule Information */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Schedule Information
            </Typography>

            <Box sx={FORM_STYLES.row}>
              <TextField
                label="Scheduled Date"
                name="scheduledDate"
                type="date"
                value={formik.values.scheduledDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.scheduledDate &&
                  Boolean(formik.errors.scheduledDate)
                }
                helperText={
                  formik.touched.scheduledDate && formik.errors.scheduledDate
                }
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
              <TextField
                label="Scheduled Time"
                name="scheduledTime"
                type="time"
                value={formik.values.scheduledTime}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.scheduledTime &&
                  Boolean(formik.errors.scheduledTime)
                }
                helperText={
                  formik.touched.scheduledTime && formik.errors.scheduledTime
                }
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Box>

            {/* Messages */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Messages
            </Typography>

            <TextField
              multiline
              minRows={3}
              label="Message for Purchased Users"
              name="messageForPurchased"
              value={formik.values.messageForPurchased}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.messageForPurchased &&
                Boolean(formik.errors.messageForPurchased)
              }
              helperText={
                formik.touched.messageForPurchased &&
                formik.errors.messageForPurchased
              }
              {...TEXT_FIELD_STYLES.standard}
            />

            <TextField
              multiline
              minRows={3}
              label="Message for Not Purchased Users"
              name="messageForNotPurchased"
              value={formik.values.messageForNotPurchased}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.messageForNotPurchased &&
                Boolean(formik.errors.messageForNotPurchased)
              }
              helperText={
                formik.touched.messageForNotPurchased &&
                formik.errors.messageForNotPurchased
              }
              {...TEXT_FIELD_STYLES.standard}
            />

            {/* Images */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Images
            </Typography>

            <Box sx={FORM_STYLES.row}>
              <TextField
                label="Faculty Image URL"
                name="facultyImage"
                value={formik.values.facultyImage}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.facultyImage &&
                  Boolean(formik.errors.facultyImage)
                }
                helperText={
                  formik.touched.facultyImage && formik.errors.facultyImage
                }
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
              />
              <TextField
                label="Image URL"
                name="imageUrl"
                value={formik.values.imageUrl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.imageUrl && Boolean(formik.errors.imageUrl)
                }
                helperText={
                  formik.touched.imageUrl && formik.errors.imageUrl
                }
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
              />
            </Box>

            {/* Tips */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Tips
            </Typography>

            {formik.values.tips &&
              formik.values.tips.map((tip, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <TextField
                    label={`Tip ${index + 1}`}
                    value={tip}
                    onChange={(e) => handleTipChange(index, e.target.value)}
                    {...TEXT_FIELD_STYLES.standard}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveTip(index)}
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddTip}
              variant="outlined"
              sx={{ mb: 2 }}
            >
              Add Tip
            </Button>

            {/* Active Status */}
            <FormControl
              {...TEXT_FIELD_STYLES.standard}
              sx={{ mt: 2 }}
            >
              <InputLabel>Active</InputLabel>
              <Select
                label="Active"
                name="active"
                value={formik.values.active ? "true" : "false"}
                onChange={(e) => {
                  formik.setFieldValue("active", e.target.value === "true");
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.active && Boolean(formik.errors.active)}
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
              {formik.touched.active && formik.errors.active && (
                <FormHelperText>{formik.errors.active}</FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box
            sx={FORM_STYLES.actions}
            style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
          >
            <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
              Cancel
            </Button>
            <Button type="submit" {...BUTTON_STYLES.primary}>
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default MasterClassModal;

